import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';

import { apiGet, apiPatch, apiPost } from '../lib/api';

interface ClientDetail {
  id: string;
  stripe_customer_id: string;
  display_name: string;
  display_email: string | null;
  internal_notes: string | null;
  default_markup_pct: number;
  invoice_brand_name: string | null;
  invoice_brand_footer: string | null;
  tags: string[];
  onboarded_at: string;
  cached_at: string;
  created_at: string;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  address_country: string | null;
  tax_id: string | null;
  tax_id_type: string | null;
  stripe_data?: {
    id: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    metadata?: Record<string, string>;
  };
}

interface Project {
  id: string;
  name: string;
  status: string;
  billing_type: string;
  markup_pct: number | null;
}

interface InvoiceRow {
  id: string;
  status: 'draft' | 'sent' | 'paid' | 'void' | 'overdue';
  total: number;
  currency: string;
}

interface CurrencyFinancialSummary {
  currency: string;
  totalInvoiced: number;
  totalPaid: number;
  outstanding: number;
}

export function ClientDetailPage() {
  const { id } = useParams();
  const [client, setClient] = useState<ClientDetail | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const [brandName, setBrandName] = useState('');
  const [brandFooter, setBrandFooter] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);
  const [savingClientInfo, setSavingClientInfo] = useState(false);

  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressPostalCode, setAddressPostalCode] = useState('');
  const [addressCountry, setAddressCountry] = useState('');
  const [taxId, setTaxId] = useState('');
  const [taxIdType, setTaxIdType] = useState('other');

  const [showNewProject, setShowNewProject] = useState(false);
  const [projName, setProjName] = useState('');
  const [projDesc, setProjDesc] = useState('');
  const [projBillingType, setProjBillingType] = useState('usage_based');
  const [projBillingCycle, setProjBillingCycle] = useState('monthly');
  const [projMarkup, setProjMarkup] = useState('');
  const [projCap, setProjCap] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  useEffect(() => {
    if (!id) return;
    void loadData();
  }, [id]);

  async function loadData(): Promise<void> {
    if (!id) return;
    setError(null);
    try {
      const [clientData, projectsData, invoiceData] = await Promise.all([
        apiGet<ClientDetail>(`/api/clients/${id}`),
        apiGet<Project[]>(`/api/clients/${id}/projects`),
        apiGet<InvoiceRow[]>(`/api/invoices?client_id=${id}`),
      ]);
      setClient(clientData);
      setProjects(projectsData);
      setInvoices(invoiceData);
      setBrandName(clientData.invoice_brand_name ?? '');
      setBrandFooter(clientData.invoice_brand_footer ?? '');
      setProjMarkup(String(Math.round(clientData.default_markup_pct * 100)));
      setAddressLine1(clientData.address_line1 ?? '');
      setAddressLine2(clientData.address_line2 ?? '');
      setAddressCity(clientData.address_city ?? '');
      setAddressState(clientData.address_state ?? '');
      setAddressPostalCode(clientData.address_postal_code ?? '');
      setAddressCountry(clientData.address_country ?? '');
      setTaxId(clientData.tax_id ?? '');
      setTaxIdType(clientData.tax_id_type ?? 'other');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load client detail');
    }
  }

  async function syncFromStripe(): Promise<void> {
    if (!id) return;
    setSyncing(true);
    try {
      await apiPost(`/api/clients/${id}/sync`, {}, { successMessage: 'Client synced from Stripe' });
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync from Stripe');
    } finally {
      setSyncing(false);
    }
  }

  async function saveBranding(): Promise<void> {
    if (!id) return;
    setSavingBrand(true);
    try {
      const updated = await apiPatch<ClientDetail>(
        `/api/clients/${id}`,
        {
          invoice_brand_name: brandName || null,
          invoice_brand_footer: brandFooter || null,
        },
        { successMessage: 'Branding saved' },
      );
      setClient(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save branding');
    } finally {
      setSavingBrand(false);
    }
  }

  async function saveClientInfo(): Promise<void> {
    if (!id) return;
    setSavingClientInfo(true);
    setError(null);
    try {
      const updated = await apiPatch<ClientDetail>(
        `/api/clients/${id}`,
        {
          address_line1: addressLine1.trim() || null,
          address_line2: addressLine2.trim() || null,
          address_city: addressCity.trim() || null,
          address_state: addressState.trim() || null,
          address_postal_code: addressPostalCode.trim() || null,
          address_country: addressCountry.trim().toUpperCase() || null,
          tax_id: taxId.trim() || null,
          tax_id_type: taxId.trim() ? taxIdType : null,
        },
        { successMessage: 'Client info saved' },
      );
      setClient(updated);
      setAddressLine1(updated.address_line1 ?? '');
      setAddressLine2(updated.address_line2 ?? '');
      setAddressCity(updated.address_city ?? '');
      setAddressState(updated.address_state ?? '');
      setAddressPostalCode(updated.address_postal_code ?? '');
      setAddressCountry(updated.address_country ?? '');
      setTaxId(updated.tax_id ?? '');
      setTaxIdType(updated.tax_id_type ?? 'other');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save client info');
    } finally {
      setSavingClientInfo(false);
    }
  }

  async function createProject(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!id || !projName.trim()) {
      setError('Project name is required');
      return;
    }
    setCreatingProject(true);
    setError(null);
    try {
      const markupPct = projMarkup ? Number(projMarkup) / 100 : undefined;
      const cap = projCap ? Number(projCap) : undefined;
      await apiPost(
        '/api/projects',
        {
          client_id: id,
          name: projName.trim(),
          description: projDesc.trim() || undefined,
          billing_type: projBillingType,
          billing_cycle: projBillingCycle,
          ...(markupPct !== undefined ? { markup_pct: markupPct } : {}),
          ...(cap !== undefined ? { monthly_cap_usd: cap } : {}),
        },
        { successMessage: 'Project created' },
      );
      setProjName('');
      setProjDesc('');
      setShowNewProject(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setCreatingProject(false);
    }
  }

  const financialSummary = useMemo(() => {
    const byCurrency = new Map<string, CurrencyFinancialSummary>();

    for (const invoice of invoices) {
      const currency = invoice.currency.toUpperCase();
      const summary = byCurrency.get(currency) ?? {
        currency,
        totalInvoiced: 0,
        totalPaid: 0,
        outstanding: 0,
      };

      summary.totalInvoiced += invoice.total;
      if (invoice.status === 'paid') {
        summary.totalPaid += invoice.total;
      }
      if (invoice.status === 'sent' || invoice.status === 'overdue') {
        summary.outstanding += invoice.total;
      }

      byCurrency.set(currency, summary);
    }

    const totalsByCurrency = Array.from(byCurrency.values()).sort((a, b) =>
      a.currency.localeCompare(b.currency),
    );

    return {
      totalsByCurrency,
      hasOutstanding: totalsByCurrency.some((item) => item.outstanding > 0),
      count: invoices.length,
    };
  }, [invoices]);

  if (!id) return <p className="text-sm text-red-600">Missing client id.</p>;

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Link to="/clients" className="text-sm text-brand-700 hover:underline">
          &larr; Clients
        </Link>
        <button
          type="button"
          onClick={() => {
            void syncFromStripe();
          }}
          disabled={syncing}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          {syncing ? 'Syncing...' : 'Sync from Stripe'}
        </button>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

      {client ? (
        <>
          <h1 className="mt-3 text-xl font-semibold text-slate-900">{client.display_name}</h1>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <SummaryCard
              label="Total Invoiced"
              value={formatCurrencySummaries(financialSummary.totalsByCurrency, 'totalInvoiced')}
            />
            <SummaryCard
              label="Paid"
              value={formatCurrencySummaries(financialSummary.totalsByCurrency, 'totalPaid')}
              accent="emerald"
            />
            <SummaryCard
              label="Outstanding"
              value={formatCurrencySummaries(financialSummary.totalsByCurrency, 'outstanding')}
              accent={financialSummary.hasOutstanding ? 'amber' : undefined}
            />
            <SummaryCard label="Invoices" value={String(financialSummary.count)} />
          </div>

          {financialSummary.totalsByCurrency.length > 1 ? (
            <p className="mt-2 text-xs text-slate-500">
              Multi-currency totals are displayed per currency (not cross-currency summed).
            </p>
          ) : null}

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <section className="rounded-lg border border-slate-200 p-4">
              <h2 className="text-sm font-semibold text-slate-700">Client Info</h2>
              <div className="mt-3 space-y-2 text-sm">
                <DetailRow label="Email" value={client.display_email ?? '-'} />
                <DetailRow
                  label="Markup"
                  value={`${Math.round(client.default_markup_pct * 100)}%`}
                />
                <DetailRow label="Onboarded" value={shortDate(client.onboarded_at)} />
                <DetailRow label="Last Synced" value={shortDate(client.cached_at)} />
                <DetailRow label="Address" value={formatAddress(client)} />
                <DetailRow label="Tax ID" value={formatTaxId(client)} />
                {client.tags.length > 0 ? (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Tags</span>
                    <div className="flex gap-1">
                      {client.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {client.internal_notes ? (
                  <DetailRow label="Notes" value={client.internal_notes} />
                ) : null}
              </div>

              <div className="mt-4 border-t border-slate-200 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Edit Address / Tax
                </p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="text-sm font-medium md:col-span-2">
                    Address Line 1
                    <input
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium md:col-span-2">
                    Address Line 2
                    <input
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    City
                    <input
                      value={addressCity}
                      onChange={(e) => setAddressCity(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    State
                    <input
                      value={addressState}
                      onChange={(e) => setAddressState(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Postal Code
                    <input
                      value={addressPostalCode}
                      onChange={(e) => setAddressPostalCode(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Country (2-letter ISO)
                    <input
                      value={addressCountry}
                      onChange={(e) => setAddressCountry(e.target.value)}
                      maxLength={2}
                      placeholder="HK"
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm uppercase"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Tax ID
                    <input
                      value={taxId}
                      onChange={(e) => setTaxId(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Tax ID Type
                    <select
                      value={taxIdType}
                      onChange={(e) => setTaxIdType(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="hk_br">hk_br</option>
                      <option value="us_ein">us_ein</option>
                      <option value="au_abn">au_abn</option>
                      <option value="cn_tin">cn_tin</option>
                      <option value="eu_vat">eu_vat</option>
                      <option value="gb_vat">gb_vat</option>
                      <option value="sg_uen">sg_uen</option>
                      <option value="other">other</option>
                    </select>
                  </label>
                </div>
                <button
                  type="button"
                  disabled={savingClientInfo}
                  onClick={() => {
                    void saveClientInfo();
                  }}
                  className="mt-3 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                >
                  {savingClientInfo ? 'Saving...' : 'Save Client Info'}
                </button>
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 p-4">
              <h2 className="text-sm font-semibold text-slate-700">Stripe Customer</h2>
              <div className="mt-3 space-y-2 text-sm">
                <DetailRow label="Stripe ID" value={client.stripe_customer_id} />
                {client.stripe_data ? (
                  <>
                    <DetailRow label="Name (Stripe)" value={client.stripe_data.name ?? '-'} />
                    <DetailRow label="Email (Stripe)" value={client.stripe_data.email ?? '-'} />
                    <DetailRow label="Phone (Stripe)" value={client.stripe_data.phone ?? '-'} />
                  </>
                ) : null}
                <a
                  href={`https://dashboard.stripe.com/customers/${client.stripe_customer_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-sm text-brand-700 underline"
                >
                  View in Stripe Dashboard
                </a>
              </div>
            </section>
          </div>

          <section className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h2 className="text-sm font-semibold text-slate-700">Invoice Branding</h2>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="text-sm font-medium">
                Brand Name
                <input
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm font-medium">
                Footer Line
                <input
                  value={brandFooter}
                  onChange={(e) => setBrandFooter(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </label>
            </div>
            <button
              type="button"
              disabled={savingBrand}
              onClick={() => {
                void saveBranding();
              }}
              className="mt-3 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
            >
              {savingBrand ? 'Saving...' : 'Save Branding'}
            </button>
          </section>

          <section className="mt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Projects</h2>
              <button
                type="button"
                onClick={() => setShowNewProject((p) => !p)}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
              >
                {showNewProject ? 'Cancel' : 'New Project'}
              </button>
            </div>

            {showNewProject ? (
              <form
                onSubmit={createProject}
                className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
              >
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-sm font-medium">
                    Name *
                    <input
                      value={projName}
                      onChange={(e) => setProjName(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                      required
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Description
                    <input
                      value={projDesc}
                      onChange={(e) => setProjDesc(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Billing Type
                    <select
                      value={projBillingType}
                      onChange={(e) => setProjBillingType(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="usage_based">Usage Based</option>
                      <option value="fixed_price">Fixed Price</option>
                      <option value="time_material">Time &amp; Material</option>
                    </select>
                  </label>
                  <label className="text-sm font-medium">
                    Billing Cycle
                    <select
                      value={projBillingCycle}
                      onChange={(e) => setProjBillingCycle(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="milestone">Milestone</option>
                      <option value="on_demand">On Demand</option>
                    </select>
                  </label>
                  <label className="text-sm font-medium">
                    Markup %
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={projMarkup}
                      onChange={(e) => setProjMarkup(e.target.value)}
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="text-sm font-medium">
                    Monthly Cap (USD)
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={projCap}
                      onChange={(e) => setProjCap(e.target.value)}
                      placeholder="Optional"
                      className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={creatingProject}
                  className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                  {creatingProject ? 'Creating...' : 'Create Project'}
                </button>
              </form>
            ) : null}

            <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Billing</th>
                    <th className="px-3 py-2">Markup</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2">
                        <Link
                          to={`/projects/${p.id}`}
                          className="font-medium text-brand-700 hover:underline"
                        >
                          {p.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="px-3 py-2 capitalize">{p.billing_type.replace('_', ' ')}</td>
                      <td className="px-3 py-2">
                        {p.markup_pct !== null ? `${Math.round(p.markup_pct * 100)}%` : '-'}
                      </td>
                    </tr>
                  ))}
                  {projects.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                        No projects yet
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}

function SummaryCard(props: { label: string; value: string; accent?: string }) {
  const colors: Record<string, string> = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
  };
  const cls = colors[props.accent ?? ''] ?? 'border-slate-200 bg-slate-50';
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <p className="text-xs text-slate-500">{props.label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900">{props.value}</p>
    </div>
  );
}

function DetailRow(props: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{props.label}</span>
      <span className="font-medium text-slate-900 text-right break-all">{props.value}</span>
    </div>
  );
}

function StatusBadge(props: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'bg-slate-100 text-slate-700',
    active: 'bg-emerald-50 text-emerald-700',
    paused: 'bg-amber-50 text-amber-700',
    completed: 'bg-blue-50 text-blue-700',
    archived: 'bg-slate-100 text-slate-500',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[props.status] ?? styles.draft}`}
    >
      {props.status}
    </span>
  );
}

function formatCurrencySummaries(
  summaries: CurrencyFinancialSummary[],
  key: 'totalInvoiced' | 'totalPaid' | 'outstanding',
): string {
  if (summaries.length === 0) {
    return '$0.00';
  }

  if (summaries.length === 1) {
    const entry = summaries[0];
    if (!entry) {
      return '$0.00';
    }
    return formatMoney(entry[key], entry.currency);
  }

  return summaries.map((entry) => formatMoneyWithCode(entry[key], entry.currency)).join(' · ');
}

function formatMoneyWithCode(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  }).format(value);
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

function formatAddress(client: ClientDetail): string {
  const lines = [client.address_line1, client.address_line2].filter(Boolean);
  const cityLine = [client.address_city, client.address_state, client.address_postal_code]
    .filter(Boolean)
    .join(' ');
  if (cityLine) lines.push(cityLine);
  if (client.address_country) lines.push(client.address_country);
  return lines.length > 0 ? lines.join(', ') : '-';
}

function formatTaxId(client: ClientDetail): string {
  if (!client.tax_id) return '-';
  const type = client.tax_id_type ?? 'other';
  return `${type}: ${client.tax_id}`;
}
